import { IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

import { TOAST_ARIA_LIVE } from './constants';
import { ToastVariant } from './types';

export interface B2ChatToastContentProps {
  variant: ToastVariant;
  message: string;
  title?: string;
  onClose: () => void;
  action?: React.ReactNode;
}

const B2ChatToastContent = forwardRef<HTMLDivElement, B2ChatToastContentProps>(
  ({ variant, message, title, onClose, action }, ref) => {
    const classes = useStyles({ hasTitle: !!title });
    const intl = useIntl();
    const ariaLive = TOAST_ARIA_LIVE[variant];

    return (
      <div ref={ref} className={classes.root} aria-live={ariaLive}>
        <Alert
          severity={variant}
          elevation={2}
          className={classes.alert}
          action={
            <>
              {action}
              <IconButton
                size="small"
                className={classes.closeButton}
                aria-label={intl.formatMessage({ id: 'AppToast.close' })}
                onClick={onClose}
              >
                <CloseIcon className={classes.closeIcon} />
              </IconButton>
            </>
          }
        >
          {title && <AlertTitle className={classes.title}>{title}</AlertTitle>}
          {title ? <span className={classes.message}>{message}</span> : message}
        </Alert>
      </div>
    );
  }
);

B2ChatToastContent.displayName = 'B2ChatToastContent';
export default B2ChatToastContent;

interface StyleProps {
  hasTitle: boolean;
}

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';
  return {
    root: {
      maxWidth: 450,
      minWidth: 300,
      fontFamily: 'Poppins, sans-serif',
    },
    alert: ({ hasTitle }: StyleProps) => ({
      width: '100%',
      alignItems: hasTitle ? 'flex-start' : 'center',
      fontSize: hasTitle ? undefined : '0.88rem',
      fontWeight: hasTitle ? undefined : 500,
      boxShadow: theme.shadows[4],
      '&.MuiAlert-standardSuccess': {
        backgroundColor: isDark ? '#0D3B2A' : '#ECFDF3',
      },
      '&.MuiAlert-standardError': {
        backgroundColor: isDark ? '#3B1419' : '#FEF3F2',
      },
      '&.MuiAlert-standardWarning': {
        backgroundColor: isDark ? '#3B2607' : '#FFFAEB',
      },
      '&.MuiAlert-standardInfo': {
        backgroundColor: isDark ? '#0D2137' : '#E3F2FD',
      },
    }),
    title: {
      fontWeight: 600,
      lineHeight: 1.2,
    },
    message: {
      fontSize: '0.85rem',
    },
    closeButton: {
      padding: 4,
    },
    closeIcon: {
      fontSize: 16,
      color: theme.palette.grey[500],
    },
  };
});
